import * as React from "react"
import Paper from "@mui/material/Paper"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TablePagination from "@mui/material/TablePagination"
import TableRow from "@mui/material/TableRow"
import { IISPRTableColumn, IISPRTableData } from "../../util/interface"
import { createUseStyles, useTheme } from "react-jss"
import { useAppSelector } from "../../util/hooks"
import styled from "@emotion/styled"
import { Typography } from "@mui/material"
const useStyles = createUseStyles({
  wrapper: {},
  root: {
    "& .MuiTableCell-head": {
      backgroundColor: "#fbfbfb",
    },
    height: 85,
  },
  heading: {
    fontSize: 16,
    fontWeight: 600,
  },
  subHeading: {
    color: "#606060",
    fontSize: 14,
    fontWeight: 600,
  },
  borderRight: {
    borderRight: "1px solid lightgray",
  },
})
const ResultDiv = styled.div`
  height: 25px;
  width: 70px;
  display: inline-block;
  padding: 2px 10px;
  border-radius: 5px;
  margin: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  margin-right: auto;
`

const PassDiv = styled(ResultDiv)`
  background-color: #27af8d;
  color: white;
`

const FailedDiv = styled(ResultDiv)`
  background-color: #fcc;
  font-size: "14px";
  background: #da1818;
`

const columns: IISPRTableColumn[] = [
  { id: "feature", label: "Feature", minWidth: 170, align: "left" },
  { id: "null_count", label: "Null Count", minWidth: 100, align: "center" },
  {
    id: "null_count_criteria",
    label: "Null Count Criteria",
    minWidth: 170,
    align: "left",
  },
  {
    id: "zero_count",
    label: "Zero Count",
    minWidth: 170,
    align: "center",
  },
  {
    id: "zero_count_criteria",
    label: "Zero Count Criteria",
    minWidth: 170,
    align: "center",
  },
  {
    id: "Null_Count_Check",
    label: "Null Count Check",
    minWidth: 170,
    align: "center",
  },
  {
    id: "Zero_Count_Check",
    label: "Zero Count Check",
    minWidth: 170,
    align: "center",
  },
]

function createData(
  feature: string,
  null_count: number,
  null_count_criteria: string,
  zero_count: number,
  zero_count_criteria: string,
  Zero_Count_Check: string,
  Null_Count_Check: string,
): IISPRTableData {
  return {
    feature,
    null_count,
    null_count_criteria,
    zero_count,
    zero_count_criteria,
    Zero_Count_Check,
    Null_Count_Check,
  }
}

export default function ISPRTable() {
  const [page, setPage] = React.useState(0) // Current page in pagination
  const [rowsPerPage, setRowsPerPage] = React.useState(10) // Rows to display per page
  const theme: any = useTheme()
  const classes = useStyles({ theme })
  const [rows, setRows] = React.useState([]) // State to hold rows for the table

  // Select ISPR data from the Redux store
  const data: IISPRTableData[] = useAppSelector(
    (state) => state.dataMetaData.isprData,
  )

  // Transform incoming data into the format required for the table
  React.useEffect(() => {
    const temp: any = data.map((row: IISPRTableData) => {
      return createData(
        row["feature"],
        row["null_count"],
        row["null_count_criteria"],
        row["zero_count"],
        row["zero_count_criteria"],
        row["Zero_Count_Check"],
        row["Null_Count_Check"],
      )
    })
    setRows(temp) // Set the transformed rows
  }, [data]) // Dependency on data changes

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage) // Update to the new page
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(+event.target.value) // Update rows per page
    setPage(0) // Reset to the first page
  }

  return (
    <Paper sx={{ width: "100%" }}>
      <TableContainer sx={{ maxHeight: 515 }}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead className={classes.root}>
            <TableRow>
              <TableCell
                key={"id"}
                align={"center"}
                className={classes.wrapper + " " + classes.borderRight}
              >
                <Typography variant="tableHeader"> S.No </Typography>
              </TableCell>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  className={classes.wrapper}
                >
                  <Typography variant="tableHeader">{column.label}</Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Render rows for the current page */}
            {rows
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, index) => {
                return (
                  <TableRow hover role="checkbox" tabIndex={-1} key={index}>
                    <TableCell align={"center"} className={classes.borderRight}>
                      {page * rowsPerPage + index + 1}
                    </TableCell>
                    {columns.map((column) => {
                      const value = row[column.id] // Get the cell value
                      return (
                        <TableCell
                          className={classes.wrapper}
                          key={column.id}
                          align={column.align}
                        >
                          {/* Format the value if it's a number, or render status indicators */}
                          {column.format && typeof value === "number" ? (
                            column.format(value)
                          ) : value === "PASS" ? (
                            <PassDiv>Pass</PassDiv>
                          ) : value === "FAIL" ? (
                            <FailedDiv>Failed</FailedDiv>
                          ) : (
                            value
                          )}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                )
              })}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Pagination component for navigating through pages */}
      <TablePagination
        rowsPerPageOptions={[10, 20]}
        component="div"
        count={data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  )
}
